import { Box, Button, Flex, Image, Text, useDisclosure } from '@chakra-ui/react';
import useToastError from 'hooks/shared/useToastError';
import { PortfolioAccountItem } from 'model/modules/portfolio/accounts';
import AppCard from 'presentation/components/shared/card/AppCard';
import FullScreenLoader from 'presentation/components/shared/FullScreenLoader';
import AlertModal from 'presentation/components/shared/modals/AlertModal';
import {
  ButtonSize,
  ButtonVariant,
} from 'presentation/theme/components/button';
import { Color } from 'presentation/theme/colorScheme';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import sharesproMark from 'assets/img/logos/sharespro-mark-green.svg';
import {
  useResumeInvestorServiceMutation,
  useStopInvestorServiceMutation,
} from 'rtkQuery/sharespro/queries/services';

interface Props {
  account: PortfolioAccountItem;
  onSuccess: () => void;
}

const InvestorServiceAutofollowCard: FC<Props> = ({ account, onSuccess }) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toastErrorHandler = useToastError();
  const [stopInvestorService, { isLoading: isStopping }] =
    useStopInvestorServiceMutation();
  const [resumeInvestorService, { isLoading: isResuming }] =
    useResumeInvestorServiceMutation();

  const isStoppable = account.service.isStoppable;
  const actionTitle = isStoppable
    ? t('autofollowActive')
    : t('autofollowPaused');
  const actionButtonText = isStoppable
    ? t('pauseAutofollow')
    : t('resumeAutofollow');
  const modalText = isStoppable
    ? t('pauseAutofollowConfirmationText')
    : t('resumeAutofollowConfirmationText');

  const handleConfirm = (): void => {
    if (account.service.productId == null) {
      return;
    }

    const request = isStoppable
      ? stopInvestorService(account.service.productId)
      : resumeInvestorService(account.service.productId);

    request
      .unwrap()
      .then(() => {
        onClose();
        onSuccess();
      })
      .catch(toastErrorHandler);
  };

  return (
    <>
      <AppCard
        minH="130px"
        p="15px"
        display="flex"
        justifyContent="space-between"
        bgImage="linear-gradient(180deg, #848484 0%, #323232 100%)"
        bgColor={Color.Primary500}
      >
        <Flex flexDirection="column" justifyContent="space-between">
          <Box>
            <Text mb="10px" color="white" fontSize="18px" fontWeight={700}>
              {actionTitle}
            </Text>
          </Box>
          <Button variant={ButtonVariant.Light} onClick={onOpen}>
            {actionButtonText}
          </Button>
        </Flex>
        <Image src={sharesproMark} h="100px" w="100px" objectFit="contain" />
      </AppCard>
      <AlertModal
        title={t('attention')}
        text={modalText}
        isOpen={isOpen}
        onClose={onClose}
        footer={
          <Flex>
            <Button
              variant={ButtonVariant.Ghost}
              mr={3}
              size={ButtonSize.sm}
              onClick={onClose}
            >
              {t('back')}
            </Button>
            <Button
              size={ButtonSize.sm}
              onClick={handleConfirm}
              isDisabled={account.service.productId == null}
            >
              {t('confirm')}
            </Button>
          </Flex>
        }
      />
      <FullScreenLoader isLoading={isStopping || isResuming} />
    </>
  );
};

export default InvestorServiceAutofollowCard;
